import { ArrowLeft } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import Markdown from "react-markdown";
import { keys } from "../../../api/queryKeys.ts";
import { api } from "../../../api/client.ts";
import CenteredSpinner from "../../../components/CenteredSpinner.tsx";
import FormattedDate from "../../../components/FormattedDate.tsx";
import SectionCard from "../../../components/SectionCard.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import { formatBytes } from "../../../utils/format.ts";

interface ScrapedPagesSectionProps {
  companyId: string;
}

const INITIAL_PAGE_LIMIT = 5;

export default function ScrapedPagesSection({ companyId }: ScrapedPagesSectionProps) {
  const [selectedPage, setSelectedPage] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const crawlPages = useQuery({
    queryKey: keys.masterCompany.crawlPages(companyId),
    queryFn: () => api.getCrawlPages(companyId),
    retry: false,
  });

  const pageContent = useQuery({
    queryKey: keys.masterCompany.crawlPageContent(companyId, selectedPage),
    queryFn: () => api.getCrawlPageContent(companyId, selectedPage!),
    enabled: !!selectedPage,
  });

  if (
    crawlPages.isError ||
    crawlPages.isLoading ||
    !crawlPages.data?.pages.length
  ) {
    return null;
  }

  return (
    <SectionCard title="Scraped pages">
      {!selectedPage && (
        <>
          <ScrollTable>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>URL</TableCell>
                <TableCell align="right">
                  Size
                  {crawlPages.data.total_size_bytes_markdown != null &&
                    ` (${formatBytes(crawlPages.data.total_size_bytes_markdown)})`}
                </TableCell>
                <TableCell>Crawled</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(showAll
                ? crawlPages.data.pages
                : crawlPages.data.pages.slice(0, INITIAL_PAGE_LIMIT)
              ).map((page) => (
                <TableRow
                  key={page.markdown_path}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => setSelectedPage(page.markdown_path)}
                >
                  <TableCell>{page.title || page.markdown_path}</TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 300,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {page.url}
                  </TableCell>
                  <TableCell align="right">
                    {formatBytes(page.size_bytes_markdown) || "-"}
                  </TableCell>
                  <TableCell>
                    <FormattedDate value={page.crawled_at} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ScrollTable>
          {!showAll && crawlPages.data.pages.length > INITIAL_PAGE_LIMIT && (
            <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
              <Button size="small" onClick={() => setShowAll(true)}>
                Show more ({crawlPages.data.pages.length - INITIAL_PAGE_LIMIT} more)
              </Button>
            </Box>
          )}
        </>
      )}
      {selectedPage && (
        <Box>
          <Button
            startIcon={<ArrowLeft />}
            onClick={() => setSelectedPage(null)}
            sx={{ mb: 2 }}
            size="small"
          >
            Back to page list
          </Button>
          {pageContent.isLoading && <CenteredSpinner py={3} />}
          {pageContent.data && (
            <>
              {pageContent.data.title && (
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  {pageContent.data.title}
                </Typography>
              )}
              <Box
                sx={{
                  p: 2,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  overflow: "auto",
                  maxHeight: 600,
                  fontSize: "0.875rem",
                  "& h1": { fontSize: "1.25rem", mt: 2, mb: 1 },
                  "& h2": { fontSize: "1.1rem", mt: 2, mb: 1 },
                  "& h3": { fontSize: "1rem", mt: 1.5, mb: 0.5 },
                  "& p": { mb: 1 },
                  "& ul, & ol": { pl: 3, mb: 1 },
                  "& a": { color: "primary.main" },
                  "& img": { maxWidth: "100%" },
                  "& pre": {
                    overflow: "auto",
                    p: 1,
                    bgcolor: "background.paper",
                    borderRadius: 1,
                  },
                }}
              >
                <Markdown>{pageContent.data.markdown}</Markdown>
              </Box>
            </>
          )}
          {pageContent.isError && (
            <Typography color="error" variant="body2">
              Failed to load page content.
            </Typography>
          )}
        </Box>
      )}
    </SectionCard>
  );
}
